/*
 * Loby editorial carousel v3 — centre-stage, infinite loop.
 * A large image centred, thin slivers of the prev/next photo at the edges.
 * Advance by hovering a sliver (arrow fades in) and clicking, by swiping, by
 * the dots, or with the arrow keys when the stage is focused. Wraps both ways
 * with no visible jump (one clone card on each end). Starts on `data-start`.
 * The active "motion" card gets a slow Ken-Burns zoom.
 */
(function () {
  if (window.__lobyEdCarouselLoaded) return;
  window.__lobyEdCarouselLoaded = true;

  function initEd2(root) {
    if (root.dataset.ed2Ready) return;
    root.dataset.ed2Ready = '1';

    const stage = root.querySelector('[data-ed2-stage]');
    const track = root.querySelector('[data-ed2-track]');
    const bar = root.querySelector('[data-ed2-bar] span');
    const originals = Array.from(track.querySelectorAll('[data-ed2-card]'));
    const N = originals.length;
    if (!stage || !track || N === 0) return;

    // one clone on each end for the wrap
    const firstClone = originals[0].cloneNode(true);
    const lastClone = originals[N - 1].cloneNode(true);
    firstClone.dataset.clone = '1';
    lastClone.dataset.clone = '1';
    track.insertBefore(lastClone, originals[0]);
    track.appendChild(firstClone);
    const cards = Array.from(track.querySelectorAll('[data-ed2-card]')); // length N + 2

    const startAttr = parseInt(root.dataset.start, 10);
    let index = Math.min(Math.max(isNaN(startAttr) ? 3 : startAttr, 1), N); // 1..N
    let settling = false;
    let dragging = false;
    let moved = false;
    let pointerId = null;
    let startX = 0;
    let baseX = 0;
    const bornAt = Date.now();
    const THRESHOLD = 24;

    const centreOffset = () => {
      const c = cards[index];
      return stage.clientWidth / 2 - (c.offsetLeft + c.offsetWidth / 2);
    };

    const logical = () => ((index - 1 + N) % N);

    const paint = (animate) => {
      track.style.transition = animate === false ? 'none' : '';
      track.style.transform = `translate3d(${centreOffset()}px, 0, 0)`;
      cards.forEach((c, i) => {
        c.classList.toggle('is-active', i === index);
        c.classList.toggle('is-side', Math.abs(i - index) === 1);
        c.classList.toggle('is-prev', i === index - 1);
        c.classList.toggle('is-next', i === index + 1);
      });
      if (bar) bar.style.width = `${((logical() + 1) / N) * 100}%`;
    };

    const reseat = () => {
      if (index < 1 || index > N) {
        index = ((index - 1 + N) % N) + 1;
        paint(false);
      }
      settling = false;
    };

    const go = (delta) => {
      index += delta;
      paint(true);
      settling = true;
      clearTimeout(go._t);
      go._t = setTimeout(reseat, 620);
    };

    // ---- edge zones + liquid-glass cursor ----
    const cursor = root.querySelector('[data-ed2-cursor]');
    if (cursor) {
      // fixed-positioned + moved to <body> so no ancestor overflow clips it.
      // drop any stale cursor left by a previous init (e.g. theme-editor reload).
      document.querySelectorAll('body > [data-ed2-cursor]').forEach((el) => el.remove());
      document.body.appendChild(cursor);
    }
    const moveCursor = (e) => {
      if (!cursor) return;
      cursor.style.left = e.clientX + 'px';
      cursor.style.top = e.clientY + 'px';
    };
    root.querySelectorAll('[data-ed2-edge]').forEach((btn) => {
      const dir = Number(btn.dataset.ed2Edge);
      btn.addEventListener('click', () => go(dir));
      btn.addEventListener('pointerenter', (e) => {
        if (!cursor) return;
        cursor.classList.toggle('is-prev', dir < 0);
        cursor.classList.toggle('is-next', dir > 0);
        moveCursor(e);
        cursor.classList.add('is-visible');
      });
      btn.addEventListener('pointermove', moveCursor);
      btn.addEventListener('pointerleave', () => {
        if (cursor) cursor.classList.remove('is-visible');
      });
    });

    // ---- keyboard ----
    stage.addEventListener('keydown', (e) => {
      if (e.target !== stage) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    });

    // ---- side card click ----
    cards.forEach((c, i) => {
      c.addEventListener('click', (e) => {
        if (moved || Date.now() - bornAt < 600) return;
        if (Math.abs(i - index) === 1 && !e.target.closest('a') && !e.target.closest('[data-ed2-edge]')) {
          e.preventDefault();
          go(i - index);
        }
      });
    });

    // ---- drag / swipe (tap-safe) ----
    const onMove = (e) => {
      if (e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;
        dragging = true;
        baseX = centreOffset();
        track.style.transition = 'none';
      }
      moved = true;
      track.style.transform = `translate3d(${baseX + dx}px, 0, 0)`;
    };
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > stage.clientWidth * 0.1) go(dx < 0 ? 1 : -1);
      else paint(true);
      if (moved) stage.addEventListener('click', (ev) => ev.stopPropagation(), { capture: true, once: true });
    };
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      dragging = false;
      moved = false;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });

    window.addEventListener('resize', () => paint(false));
    if ('fonts' in document) document.fonts.ready.then(() => paint(false));
    cards.forEach((c) => c.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', () => { if (!settling && !dragging) paint(false); }, { once: true });
    }));
    paint(false);
  }

  // ---- lightweight strip pager (the "Pair with" section) ----
  function initStrip(root) {
    if (root.dataset.lobyStripReady) return;
    root.dataset.lobyStripReady = '1';
    const strip = root.querySelector('[data-loby-editorial-strip]');
    const fill = root.querySelector('[data-loby-editorial-progress] span');
    if (!strip) return;
    const max = () => Math.max(strip.scrollWidth - strip.clientWidth, 0);
    const render = () => {
      const m = max();
      if (fill) fill.style.width = `${(m > 0 ? strip.scrollLeft / m : 0) * 100 || 6}%`;
      root.querySelectorAll('.loby-editorial-arrow[data-dir="-1"]').forEach((b) => (b.disabled = strip.scrollLeft <= 2));
      root.querySelectorAll('.loby-editorial-arrow[data-dir="1"]').forEach((b) => (b.disabled = strip.scrollLeft >= m - 2));
    };
    root.querySelectorAll('.loby-editorial-arrow').forEach((btn) => {
      btn.addEventListener('click', () => {
        const first = strip.querySelector(':scope > *');
        const by = (first ? first.getBoundingClientRect().width + 16 : strip.clientWidth) * Number(btn.dataset.dir);
        const target = Math.max(0, Math.min(strip.scrollLeft + by, max()));
        try { strip.scrollTo({ left: target, behavior: 'smooth' }); } catch (e) { strip.scrollLeft = target; }
        setTimeout(() => { if (Math.abs(strip.scrollLeft - target) > 2) strip.scrollLeft = target; render(); }, 400);
      });
    });
    strip.addEventListener('scroll', render, { passive: true });
    window.addEventListener('resize', render);
    render();
  }

  function boot() {
    document.querySelectorAll('[data-ed2]').forEach(initEd2);
    document.querySelectorAll('[data-loby-editorial]').forEach(initStrip);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
